import React, { useState } from "react";
import { Box, Grid, TextField, Typography, Popover } from "@mui/material";
import { ChromePicker } from "react-color";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

const ThemeFieldInput = ({
  label = "",
  value = "",
  onChange = () => {},
  disabled = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [touched, setTouched] = useState(false);

  const formatLabel = (key) =>
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/\s+/g, "");

  const isRadius = label.toLowerCase().includes("radius");
  const hexColorRegex =
    /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{4}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/;
  const radiusNumeric = isRadius
    ? parseFloat(String(value).replace(/px$/, ""))
    : null;
  const radiusValid = isRadius
    ? value !== "" &&
      !isNaN(radiusNumeric) &&
      radiusNumeric >= 0 &&
      radiusNumeric <= 100
    : true;
  const isValid = isRadius ? radiusValid : hexColorRegex.test(value);

  const handleBoxClick = (event) => {
    if (!isRadius) setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const id = open ? "color-popover" : undefined;

  const handleChange = (v) => {
    if (isRadius) {
      if (v === "" || v === null) {
        onChange("0px");
      } else {
        onChange(`${v}px`);
      }
      return;
    }

    if (/^[A-Za-z0-9]{1}$/.test(v)) onChange("#" + v);
    else onChange(v);
  };

  const handleBlur = () => {
    setTouched(true);
  };

  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={6}>
        <Typography
          fontWeight={500}
          sx={{
            color:
              theme.palette.mode === "dark"
                ? "var(--color-4)"
                : "var(--color-34)",
          }}
        >
          {t(`uiCustomization.formBuilder.${formatLabel(label)}`)}
        </Typography>
      </Grid>

      {!isRadius && (
        <Grid item>
          <Box
            onClick={handleBoxClick}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "4px",
              border: "1px solid var(--color-37)",
              cursor: "pointer",
              backgroundColor: value,
              pointerEvents: disabled ? "none" : "auto",
            }}
          />
        </Grid>
      )}

      <Grid item xs>
        <TextField
          dir="ltr"
          disabled={disabled}
          size="small"
          fullWidth
          type={isRadius ? "number" : "text"}
          value={isRadius ? parseFloat(value) || 0 : value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          inputProps={{
            maxLength: isRadius ? undefined : 9,
            min: isRadius ? 0 : undefined,
            max: isRadius ? 100 : undefined,
          }}
          error={touched && !isValid}
        />
      </Grid>

      {!isRadius && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <ChromePicker
            color={value}
            onChangeComplete={(color) => handleChange(color.hex)}
            disableAlpha
          />
        </Popover>
      )}
    </Grid>
  );
};

export default ThemeFieldInput;
